import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const LS_KEY = "l2f_intro_walkthrough_seen";

function readLS(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(LS_KEY) === "true";
  } catch {
    return true;
  }
}

function writeLS(v: boolean) {
  if (typeof window === "undefined") return;
  try {
    if (v) window.localStorage.setItem(LS_KEY, "true");
    else window.localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Wave 4 Commit 1 — 3-screen value walkthrough state.
 *
 * Storage: hybrid
 *   - localStorage `l2f_intro_walkthrough_seen` (fast, sync — avoids FOUC)
 *   - profiles.intro_walkthrough_seen_at (durable, cross-device)
 *
 * Reconciliation: when userId flips from null → id, we pull the DB row and
 * only *upgrade* the local flag to seen (never downgrade); reset() flows
 * from user action.
 */
export function useOnboardingState(userId: string | null | undefined) {
  const [hasSeen, setHasSeen] = useState<boolean>(() => readLS());

  // Reconcile with DB when we know the user.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await (supabase.from("profiles") as any)
        .select("intro_walkthrough_seen_at")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      const dbSeen = !!data?.intro_walkthrough_seen_at;
      if (dbSeen && !hasSeen) {
        writeLS(true);
        setHasSeen(true);
      } else if (!dbSeen && hasSeen) {
        // Local says seen but DB says not — trust local, sync DB.
        await (supabase.from("profiles") as any)
          .update({ intro_walkthrough_seen_at: new Date().toISOString() })
          .eq("id", userId);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const complete = useCallback(async () => {
    writeLS(true);
    setHasSeen(true);
    if (userId) {
      await (supabase.from("profiles") as any)
        .update({ intro_walkthrough_seen_at: new Date().toISOString() })
        .eq("id", userId);
    }
  }, [userId]);

  const reset = useCallback(async () => {
    writeLS(false);
    setHasSeen(false);
    if (userId) {
      await (supabase.from("profiles") as any)
        .update({ intro_walkthrough_seen_at: null })
        .eq("id", userId);
    }
  }, [userId]);

  return { hasSeen, shouldShow: !hasSeen, complete, reset };
}
